import {CheckIcon} from '@primer/octicons-react'
import {ActionList, Label} from '@primer/react'
import React from 'react'

import {HighlightedText} from './HighlightedText'
import {useNavigate} from '@github-ui/use-navigate'

import styles from './RefItem.module.css'
import {isUIService} from '@github-ui/runtime-environment'

interface RefItemProps {
  href?: string
  isCurrent?: boolean
  isDefault?: boolean
  gitRef: string
  filterText: string
  ariaPosInSet?: number
  ariaSetSize?: number
  onSelect?: (gitRef: string) => void
  onClick?: (gitRef: string) => void
  shouldSetAsDiv?: boolean
  isPendingTag?: boolean
}

export const RefItem = React.memo(function RefItemInner({
  isCurrent,
  isDefault,
  href,
  gitRef,
  filterText,
  ariaPosInSet,
  ariaSetSize,
  onSelect,
  onClick,
  shouldSetAsDiv = false,
  isPendingTag = false,
}: RefItemProps) {
  const innerRefItem = (
    <RefItemContent
      gitRef={gitRef}
      isDefault={isDefault}
      isCurrent={isCurrent}
      filterText={filterText}
      isPendingTag={isPendingTag}
    />
  )

  const commonProps = {
    'aria-posinset': shouldSetAsDiv ? undefined : ariaPosInSet,
    'aria-setsize': shouldSetAsDiv ? undefined : ariaSetSize,
    'aria-checked': isCurrent,
    sx: {minWidth: 0},
    onSelect: () => {
      onSelect?.(gitRef)
      if (href) {
        navigate(href, {reloadDocument: !isUIService})
      }
    },
    onClick: () => {
      onClick?.(gitRef)
      if (href) {
        navigate(href, {reloadDocument: !isUIService})
      }
    },
  }

  const navigate = useNavigate()

  return (
    <ActionList.Item role="menuitemradio" {...commonProps}>
      {innerRefItem}
      {isPendingTag && (
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        <ActionList.TrailingVisual className="fg-muted mr-1">Created on publish</ActionList.TrailingVisual>
      )}
    </ActionList.Item>
  )
})

interface RefItemContentProps extends RefItemProps {
  showLeadingVisual?: boolean
}

export const RefItemContent = React.memo(function RefItemInner({
  isCurrent,
  isDefault,
  gitRef,
  filterText,
  showLeadingVisual = true,
}: RefItemContentProps) {
  return (
    <div style={{display: 'flex', justifyContent: 'space-between'}}>
      <div style={{display: 'flex', minWidth: 0, alignItems: 'flex-end'}}>
        {showLeadingVisual && (
          <CheckIcon aria-hidden data-icon-visibility={isCurrent ? 'visible' : 'hidden'} className={styles.ItemIcon} />
        )}
        <HighlightedText key={gitRef} search={filterText} text={gitRef} wrap />
      </div>
      {isDefault && <Label>default</Label>}
    </div>
  )
})
